/**
 * Admin "Edit arc" page (Refinement Phase A3).
 *
 * Seven-field full-row edit form against PATCH /v2/staging/arcs/:id:
 *   • slug             — non-empty, [a-z0-9][a-z0-9-]{0,119}
 *   • title            — trimmed non-empty, ≤ 500 chars
 *   • arc_type         — closed allowlist: season | special | spinoff
 *   • status           — closed allowlist: scheduled | active | completed
 *   • display_order    — nullable integer (empty input = null)
 *   • start_date       — nullable yyyy-mm-dd (empty input = null)
 *   • end_date         — nullable yyyy-mm-dd; merged-shape pre-check
 *   • idempotency_key  — crypto.randomUUID() per form open
 *
 * Locked scope (per FISHSTANK_V2_ARC_EDIT_FIRST_SLICE.md):
 *   - No `winner_entity_id` editing — deferred to Phase B per audit §4.
 *   - No `summary_md` — owned by CP10 `/wiki/arcs/:id/edit`.
 *   - No delete.
 *
 * Idempotency: one crypto.randomUUID() per form open, reused across
 * retries (double-click safety / replay-stable).
 *
 * Delta posture: only the fields that changed vs the loaded
 * snapshot are sent to the worker, mirroring CP17's entity-edit
 * delta-only PATCH so the parser only sees what's intended to flip.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import type {
  ArcEditRequest,
  ArcMutationReceipt,
  ArcParticipationAdminItem,
  ArcParticipationCreateRequest,
  ArcParticipationDeleteReceipt,
  ArcParticipationEditRequest,
  ArcParticipationMutationReceipt,
  ArcParticipationValidationError,
  ArcRowSnapshot,
  ArcStatusEditable,
  ArcTypeEditable,
  ArcValidationError,
  EntitiesAdminListItem,
  EntityKindAdmin,
  ParticipationRoleEditable,
  ParticipationStatusEditable,
} from '@fishstank/contracts'
import {
  ARC_STATUSES_EDITABLE,
  ARC_TYPES_EDITABLE,
  ENTITY_KINDS_ADMIN,
  PARTICIPATION_STATUSES_EDITABLE,
} from '@fishstank/contracts'

// Default role for newly-created arc participations.
//
// The Arc participant taxonomy correction removed the operator-facing
// role <select> from this surface; the column still lives in the DB
// (with default 'competitor') and the contract still requires a value
// on POST, so the form sends this value verbatim.
//
// Future cleanup: drop the column entirely once all consumers
// (audit replays, tests, exports) have been confirmed to no longer
// read it.
const PARTICIPATION_ROLE_DEFAULT: ParticipationRoleEditable = 'competitor'

import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'
import {
  type ArcMutationFetchResult,
  type ArcParticipationMutationFetchResult,
  deleteArcParticipation,
  fetchArcForAdmin,
  fetchArcParticipationsForAdmin,
  fetchEntitiesForAdmin,
  fetchEntityAliasesForAdmin,
  patchArc,
  patchArcParticipation,
  postArcParticipation,
} from '../lib/worker'

function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

/**
 * User-facing label for an entity kind, capitalized to match the
 * casing convention used elsewhere on the public + admin surfaces
 * (Fish / Freeloader / Host / Production / Other). Falls back to
 * the raw enum string for any forward-compat value the UI doesn't
 * yet know about.
 */
function entityKindLabel(kind: string): string {
  switch (kind) {
    case 'fish':
      return 'Fish'
    case 'freeloader':
      return 'Freeloader'
    case 'host':
      return 'Host'
    case 'production':
      return 'Production'
    case 'other':
      return 'Other'
    default:
      return kind
  }
}

/**
 * Map a typed validation error to the field name the SPA renders
 * the inline message under. Form-level errors (those without a
 * specific field anchor) return null and surface in a banner.
 */
function fieldFromError(err: ArcValidationError): string | null {
  switch (err.code) {
    case 'slug_required':
    case 'slug_too_long':
    case 'invalid_slug':
    case 'slug_conflict':
      return 'slug'
    case 'title_required':
    case 'title_too_long':
      return 'title'
    case 'invalid_enum':
      return err.field
    case 'invalid_uuid':
    case 'idempotency_key_in_use':
      return 'idempotency_key'
    case 'another_arc_active':
      return 'status'
    case 'invalid_integer':
      return 'display_order'
    case 'invalid_date':
      return err.field
    case 'end_before_start':
      return 'end_date'
    case 'invalid_body':
    case 'no_editable_fields':
    case 'arc_not_found':
      return null
    default:
      return null
  }
}

function validationMessage(err: ArcValidationError): string {
  switch (err.code) {
    case 'invalid_body':
      return 'Request body must be a JSON object.'
    case 'slug_required':
      return 'Slug is required.'
    case 'slug_too_long':
      return `Slug is too long (max ${err.max} characters).`
    case 'invalid_slug':
      return 'Slug must be lowercase letters, digits, and hyphens; must start with a letter or digit.'
    case 'title_required':
      return 'Title is required.'
    case 'title_too_long':
      return `Title is too long (max ${err.max} characters).`
    case 'invalid_enum':
      return `"${err.field}" is not allowed (expected one of: ${err.allowed.join(', ')}).`
    case 'invalid_uuid':
      return 'Internal error: idempotency key must be a UUID. Refresh and try again.'
    case 'slug_conflict':
      return 'Another arc already uses this slug.'
    case 'another_arc_active':
      return 'Another arc is already marked active. Set this one to "scheduled" or "completed", or end the active arc first.'
    case 'idempotency_key_in_use':
      return 'This form was already submitted for a different request. Refresh to start a new edit.'
    case 'no_editable_fields':
      return 'Nothing changed. Modify at least one field before submitting.'
    case 'arc_not_found':
      return 'This arc no longer exists. It may have been deleted by another operator.'
    case 'invalid_date':
      return `"${err.field}" must be a date in YYYY-MM-DD format.`
    case 'invalid_integer':
      return 'Display order must be a whole number (or empty for none).'
    case 'end_before_start':
      return 'End date must be on or after the start date.'
    default:
      return 'Validation failed.'
  }
}

/**
 * Normalize an HTML <input type="number"> value to `number | null`.
 * Empty string → null. Non-integer-looking → null (form validation
 * will block before submission).
 */
function parseDisplayOrderInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  return Number.isInteger(n) ? n : null
}

/**
 * Normalize an HTML <input type="date"> value to `string | null`.
 * Empty string → null; otherwise the yyyy-mm-dd value verbatim.
 */
function parseDateInput(raw: string): string | null {
  return raw.trim() === '' ? null : raw
}

export function ArcEditPage(): JSX.Element {
  const params = useParams<{ id: string }>()
  const id = params.id ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const idLooksValid = /^\d+$/.test(id)

  const query = useQuery({
    queryKey: ['arc', 'admin', id],
    queryFn: () => fetchArcForAdmin(id),
    enabled: idLooksValid,
  })

  // Pinned per form-open. Stable across renders so a re-render or
  // React-Query cache invalidation doesn't accidentally flip the key.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [id])

  // Local edit state (lazily seeded once the row loads).
  const [slug, setSlug] = useState<string | null>(null)
  const [title, setTitle] = useState<string | null>(null)
  const [arcType, setArcType] = useState<ArcTypeEditable | null>(null)
  const [status, setStatus] = useState<ArcStatusEditable | null>(null)
  const [displayOrder, setDisplayOrder] = useState<string | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)

  const [validationError, setValidationError] =
    useState<ArcValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<ArcRowSnapshot | null>(null)

  const result = query.data
  const loaded =
    result && result.kind === 'ok' ? result.artifact.body.arc : null

  // First-render after load: seed local state.
  const seeded = slug !== null
  if (loaded && !seeded) {
    setSlug(loaded.slug)
    setTitle(loaded.title)
    // The DB enum may carry 'off_season' (legacy) or 'cancelled' on
    // an existing row even though those are not in the EDITABLE
    // allowlists. Fall back to a defensible default but render the
    // legacy value once in the dropdown for round-trip.
    setArcType(
      (ARC_TYPES_EDITABLE as readonly string[]).includes(loaded.arc_type)
        ? (loaded.arc_type as ArcTypeEditable)
        : 'season',
    )
    setStatus(
      (ARC_STATUSES_EDITABLE as readonly string[]).includes(loaded.status)
        ? (loaded.status as ArcStatusEditable)
        : 'scheduled',
    )
    setDisplayOrder(
      loaded.display_order === null ? '' : String(loaded.display_order),
    )
    setStartDate(loaded.start_date ?? '')
    setEndDate(loaded.end_date ?? '')
  }

  /**
   * Build the delta from local state vs the loaded row. Only fields
   * that genuinely changed are included. Empty inputs map to null
   * for nullable columns; absent changes map to "no edit."
   */
  const buildDelta = (): ArcEditRequest | null => {
    if (!loaded) return null
    const body: ArcEditRequest = { idempotency_key: idempotencyKey }

    if (slug !== null && slug.trim() !== loaded.slug) {
      body.slug = slug.trim()
    }
    if (title !== null && title.trim() !== loaded.title) {
      body.title = title.trim()
    }
    if (arcType !== null && arcType !== loaded.arc_type) {
      body.arc_type = arcType
    }
    if (status !== null && status !== loaded.status) {
      body.status = status
    }
    if (displayOrder !== null) {
      const parsed = parseDisplayOrderInput(displayOrder)
      if (parsed !== loaded.display_order) {
        body.display_order = parsed
      }
    }
    if (startDate !== null) {
      const parsed = parseDateInput(startDate)
      if (parsed !== loaded.start_date) {
        body.start_date = parsed
      }
    }
    if (endDate !== null) {
      const parsed = parseDateInput(endDate)
      if (parsed !== loaded.end_date) {
        body.end_date = parsed
      }
    }

    return body
  }

  const mutation = useMutation<
    ArcMutationFetchResult<ArcMutationReceipt>,
    unknown,
    ArcEditRequest
  >({
    mutationFn: (body) => patchArc(id, { body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        setReceipt(res.artifact.body.row)
        queryClient.invalidateQueries({ queryKey: ['arc', 'admin', id] })
        queryClient.invalidateQueries({ queryKey: ['arcs', 'admin', 'list'] })
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setValidationError({ code: 'arc_not_found' })
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setValidationError(null)
    setNetworkError(null)
    const body = buildDelta()
    if (!body) return
    const editableKeys = Object.keys(body).filter((k) => k !== 'idempotency_key')
    if (editableKeys.length === 0) {
      setValidationError({ code: 'no_editable_fields' })
      return
    }
    mutation.mutate(body)
  }

  const errorField = validationError ? fieldFromError(validationError) : null

  return (
    <Shell
      provenance={
        result && (result.kind === 'ok' || result.kind === 'empty')
          ? result.artifact.provenance
          : null
      }
    >
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Edit arc</h1>
        <p className="mt-1 text-sm text-slate-400">
          <Link to="/arcs" className="text-fishstank-accent underline-offset-2 hover:underline">
            ← Back to arcs
          </Link>
        </p>
      </header>

      {!idLooksValid && (
        <div
          data-testid="arc-edit-bad-id"
          className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
        >
          Invalid id in URL. Expected a positive integer.
        </div>
      )}

      {idLooksValid && query.isLoading && (
        <div data-testid="arc-edit-loading" className="text-slate-400">
          Loading arc…
        </div>
      )}

      {result && result.kind === 'error' && (
        <div
          data-testid="arc-edit-load-error"
          className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
        >
          {result.message}
          {result.status ? <span className="ml-2 font-mono">(HTTP {result.status})</span> : null}
        </div>
      )}

      {loaded && (
        <form data-testid="arc-edit-form" className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="arc-edit-slug" className="mb-1 block text-sm font-medium text-slate-200">
              Slug
            </label>
            <input
              id="arc-edit-slug"
              data-testid="arc-edit-slug"
              type="text"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 font-mono text-slate-100"
              value={slug ?? ''}
              onChange={(e) => setSlug(e.target.value)}
              disabled={mutation.isPending}
              pattern="[a-z0-9][-a-z0-9]*"
              maxLength={120}
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              Lowercase letters, digits, and hyphens; must start with a letter or digit.
            </p>
            {errorField === 'slug' && validationError && (
              <p
                data-testid="arc-edit-error-slug"
                data-error-code={validationError.code}
                className="mt-1 text-sm text-rose-300"
              >
                {validationMessage(validationError)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="arc-edit-title" className="mb-1 block text-sm font-medium text-slate-200">
              Title
            </label>
            <input
              id="arc-edit-title"
              data-testid="arc-edit-title"
              type="text"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={title ?? ''}
              onChange={(e) => setTitle(e.target.value)}
              disabled={mutation.isPending}
              maxLength={500}
              required
            />
            {errorField === 'title' && validationError && (
              <p
                data-testid="arc-edit-error-title"
                data-error-code={validationError.code}
                className="mt-1 text-sm text-rose-300"
              >
                {validationMessage(validationError)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="arc-edit-arc-type" className="mb-1 block text-sm font-medium text-slate-200">
              Type
            </label>
            <select
              id="arc-edit-arc-type"
              data-testid="arc-edit-arc-type"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={arcType ?? ''}
              onChange={(e) => setArcType(e.target.value as ArcTypeEditable)}
              disabled={mutation.isPending}
              required
            >
              {ARC_TYPES_EDITABLE.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {errorField === 'arc_type' && validationError && (
              <p
                data-testid="arc-edit-error-arc-type"
                data-error-code={validationError.code}
                className="mt-1 text-sm text-rose-300"
              >
                {validationMessage(validationError)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="arc-edit-status" className="mb-1 block text-sm font-medium text-slate-200">
              Status
            </label>
            <select
              id="arc-edit-status"
              data-testid="arc-edit-status"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={status ?? ''}
              onChange={(e) => setStatus(e.target.value as ArcStatusEditable)}
              disabled={mutation.isPending}
              required
            >
              {ARC_STATUSES_EDITABLE.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              "cancelled" is reserved for a future flow and not editable here.
            </p>
            {errorField === 'status' && validationError && (
              <p
                data-testid="arc-edit-error-status"
                data-error-code={validationError.code}
                className="mt-1 text-sm text-rose-300"
              >
                {validationMessage(validationError)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="arc-edit-display-order" className="mb-1 block text-sm font-medium text-slate-200">
              Display order
            </label>
            <input
              id="arc-edit-display-order"
              data-testid="arc-edit-display-order"
              type="number"
              step="1"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={displayOrder ?? ''}
              onChange={(e) => setDisplayOrder(e.target.value)}
              disabled={mutation.isPending}
            />
            <p className="mt-1 text-xs text-slate-500">
              Leave blank to clear (NULL).
            </p>
            {errorField === 'display_order' && validationError && (
              <p
                data-testid="arc-edit-error-display-order"
                data-error-code={validationError.code}
                className="mt-1 text-sm text-rose-300"
              >
                {validationMessage(validationError)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="arc-edit-start-date" className="mb-1 block text-sm font-medium text-slate-200">
              Start date
            </label>
            <input
              id="arc-edit-start-date"
              data-testid="arc-edit-start-date"
              type="date"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={startDate ?? ''}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={mutation.isPending}
            />
            {errorField === 'start_date' && validationError && (
              <p
                data-testid="arc-edit-error-start-date"
                data-error-code={validationError.code}
                className="mt-1 text-sm text-rose-300"
              >
                {validationMessage(validationError)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="arc-edit-end-date" className="mb-1 block text-sm font-medium text-slate-200">
              End date
            </label>
            <input
              id="arc-edit-end-date"
              data-testid="arc-edit-end-date"
              type="date"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={endDate ?? ''}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={mutation.isPending}
            />
            {errorField === 'end_date' && validationError && (
              <p
                data-testid="arc-edit-error-end-date"
                data-error-code={validationError.code}
                className="mt-1 text-sm text-rose-300"
              >
                {validationMessage(validationError)}
              </p>
            )}
          </div>

          {validationError && errorField === null && (
            <div
              data-testid="arc-edit-validation-error"
              data-error-code={validationError.code}
              className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
            >
              <strong>{validationError.code}</strong>
              <span className="ml-2">{validationMessage(validationError)}</span>
            </div>
          )}

          {networkError && (
            <div
              data-testid="arc-edit-network-error"
              className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
            >
              {networkError}
            </div>
          )}

          {receipt && (
            <div
              data-testid="arc-edit-receipt"
              className="rounded border border-emerald-700 bg-emerald-950/40 p-4 text-emerald-100"
            >
              Saved.{' '}
              <strong data-testid="arc-edit-receipt-title">{receipt.title}</strong>{' '}
              (slug <code data-testid="arc-edit-receipt-slug">{receipt.slug}</code>,
              type <code data-testid="arc-edit-receipt-arc-type">{receipt.arc_type}</code>,
              status <code data-testid="arc-edit-receipt-status">{receipt.status}</code>).{' '}
              <Link
                to="/arcs"
                data-testid="arc-edit-back-to-arcs"
                className="underline underline-offset-2"
              >
                Back to arcs
              </Link>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              data-testid="arc-edit-submit"
              className="rounded bg-fishstank-accent px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              data-testid="arc-edit-cancel"
              className="rounded border border-slate-700 px-4 py-2 text-sm text-slate-200"
              onClick={() => navigate('/arcs')}
              disabled={mutation.isPending}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {idLooksValid && <ParticipationsSection arcId={id} />}
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Refinement Phase B1 — read-only Participations section.
//
// Renders below the existing arc edit form. Pure read; no edit /
// add / delete affordances in B1. The mutation surfaces (B2/B3)
// will hang their forms inside this section once they ship.
//
// Sort + soft-merge filter come from the worker / loader; the SPA
// renders rows verbatim.
// ────────────────────────────────────────────────────────────────────────

function ParticipationsSection({ arcId }: { arcId: string }): JSX.Element {
  const query = useQuery({
    queryKey: ['arc', 'admin', arcId, 'participations'],
    queryFn: () => fetchArcParticipationsForAdmin(arcId),
  })

  return (
    <section className="mt-10" data-testid={`arc-participations-section-${arcId}`}>
      <header className="mb-3 flex items-baseline justify-between">
        <div>
          <h2 className="text-xl font-semibold">Participations</h2>
          <p className="mt-1 text-xs text-slate-500">
            Soft-merged entities are filtered out. Editing existing rows
            (and the arc-side winner flow) lands in a future slice.
          </p>
        </div>
      </header>

      {/* Refinement Phase B2 — Add participation form. Edit and
          delete affordances on existing rows are still deferred to
          B3. */}
      <AddParticipationForm arcId={arcId} />

      <FetchedArtifact
        result={query.data}
        isLoading={query.isLoading}
        testidPrefix="arc-participations"
        labels={{
          loading: 'Loading participations…',
          errorTitle: "Couldn't load participations.",
          empty: 'No participants yet.',
        }}
      >
        {(artifact) => (
          <ParticipationsTable
            arcId={arcId}
            participations={artifact.body.participations}
          />
        )}
      </FetchedArtifact>
    </section>
  )
}

interface ParticipationsTableProps {
  arcId: string
  participations: readonly ArcParticipationAdminItem[]
}

function ParticipationsTable({ arcId, participations }: ParticipationsTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid={`arc-participations-table-${arcId}`}
        data-participation-count={participations.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Entity</th>
            <th className="px-3 py-2 text-left font-medium">Slug</th>
            <th className="px-3 py-2 text-left font-medium">Category</th>
            <th className="px-3 py-2 text-left font-medium">Alias</th>
            <th className="px-3 py-2 text-left font-medium">Status</th>
            <th className="px-3 py-2 text-left font-medium">Joined</th>
            <th className="px-3 py-2 text-left font-medium">Left</th>
            <th className="px-3 py-2 text-left font-medium">Notes</th>
            <th className="px-3 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {participations.map((p) => (
            <ParticipationRow key={p.id} arcId={arcId} participation={p} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function statusChipClass(status: ArcParticipationAdminItem['status']): string {
  if (status === 'winner') {
    return 'inline-block rounded-full bg-amber-900/50 px-2 py-0.5 text-xs font-medium text-amber-200'
  }
  if (status === 'active') {
    return 'inline-block rounded-full bg-emerald-900/50 px-2 py-0.5 text-xs font-medium text-emerald-200'
  }
  return 'inline-block rounded-full bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-300'
}

interface ParticipationRowProps {
  arcId: string
  participation: ArcParticipationAdminItem
}

function ParticipationRow({ arcId, participation }: ParticipationRowProps) {
  const [mode, setMode] = useState<'view' | 'edit' | 'confirm-delete'>('view')

  if (mode === 'edit') {
    return (
      <EditParticipationRow
        arcId={arcId}
        participation={participation}
        onCancel={() => setMode('view')}
        onSaved={() => setMode('view')}
      />
    )
  }

  return (
    <ParticipationViewRow
      arcId={arcId}
      participation={participation}
      mode={mode}
      onEdit={() => setMode('edit')}
      onAskDelete={() => setMode('confirm-delete')}
      onCancelDelete={() => setMode('view')}
      onDeleted={() => setMode('view')}
    />
  )
}

interface ParticipationViewRowProps extends ParticipationRowProps {
  mode: 'view' | 'confirm-delete'
  onEdit: () => void
  onAskDelete: () => void
  onCancelDelete: () => void
  onDeleted: () => void
}

function ParticipationViewRow({
  arcId,
  participation,
  mode,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onDeleted,
}: ParticipationViewRowProps) {
  const queryClient = useQueryClient()
  const [networkError, setNetworkError] = useState<string | null>(null)
  const idempotencyKey = useMemo(() => globalThis.crypto.randomUUID(), [participation.id, mode])

  const dateLabel = (raw: string | null): string => {
    if (raw === null) return '—'
    return raw.slice(0, 10)
  }

  const deleteMutation = useMutation<
    ArcParticipationMutationFetchResult<ArcParticipationDeleteReceipt>,
    unknown,
    void
  >({
    mutationFn: () =>
      deleteArcParticipation(participation.id, {
        body: { idempotency_key: idempotencyKey },
      }),
    onSuccess: (res) => {
      setNetworkError(null)
      if (res.kind === 'ok') {
        queryClient.invalidateQueries({
          queryKey: ['arc', 'admin', arcId, 'participations'],
        })
        // Also invalidate the arc edit form's loaded snapshot so
        // the winner_entity_id field reflects any side-effect.
        queryClient.invalidateQueries({ queryKey: ['arc', 'admin', arcId] })
        // Refinement Phase B4 — keep the entity-side participations
        // view in sync. Deleting a participation here removes it
        // from the entity's list on EntityEdit.tsx.
        queryClient.invalidateQueries({
          queryKey: ['entity-participations', participation.entity_id],
        })
        onDeleted()
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        // Treat as already-deleted (probably another tab beat us).
        queryClient.invalidateQueries({
          queryKey: ['arc', 'admin', arcId, 'participations'],
        })
        queryClient.invalidateQueries({
          queryKey: ['entity-participations', participation.entity_id],
        })
        onDeleted()
      } else if (res.kind === 'validation_error') {
        setNetworkError(`Validation: ${res.error.code}`)
      } else {
        setNetworkError(res.message)
      }
    },
  })

  return (
    <tr
      data-testid={`arc-participation-row-${participation.id}`}
      data-entity-slug={participation.entity.slug}
      data-entity-kind={participation.entity.kind}
      data-status={participation.status}
      id={`participation-${participation.id}`}
    >
      <td className="px-3 py-2 text-slate-100">
        {participation.entity.display_name}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-slate-300">
        {participation.entity.slug}
      </td>
      <td
        data-testid={`arc-participation-row-category-${participation.id}`}
        data-kind-override={participation.kind_override ?? ''}
        className="px-3 py-2 text-slate-300"
      >
        {entityKindLabel(
          participation.kind_override ?? participation.entity.kind,
        )}
        {participation.kind_override !== null && (
          <span
            data-testid={`arc-participation-row-category-override-badge-${participation.id}`}
            className="ml-2 inline-block rounded bg-indigo-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-200"
            title={`Per-arc override; entity kind is ${entityKindLabel(participation.entity.kind)}`}
          >
            override
          </span>
        )}
      </td>
      <td
        data-testid={`arc-participation-row-display-alias-${participation.id}`}
        data-display-alias-id={participation.display_alias_id ?? ''}
        className="px-3 py-2 text-slate-300"
      >
        {participation.display_alias_text === null ? (
          <span className="text-slate-600">—</span>
        ) : (
          <>
            <span>{participation.display_alias_text}</span>
            <span
              className="ml-2 inline-block rounded bg-fuchsia-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-fuchsia-200"
              title={`Per-arc display override; primary name is ${participation.entity.display_name}`}
            >
              alias
            </span>
          </>
        )}
      </td>
      <td className="px-3 py-2">
        <span className={statusChipClass(participation.status)}>
          {participation.status}
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-slate-400">
        {dateLabel(participation.joined_at)}
      </td>
      <td className="px-3 py-2 text-xs text-slate-400">
        {dateLabel(participation.left_at)}
      </td>
      <td className="px-3 py-2 text-xs text-slate-400">
        {participation.notes ?? '—'}
      </td>
      <td className="px-3 py-2 text-right">
        {mode === 'view' && (
          <>
            <button
              type="button"
              data-testid={`arc-participation-edit-button-${participation.id}`}
              className="mr-2 text-fishstank-accent underline-offset-2 hover:underline"
              onClick={onEdit}
            >
              Edit
            </button>
            <button
              type="button"
              data-testid={`arc-participation-delete-button-${participation.id}`}
              className="text-rose-300 underline-offset-2 hover:underline"
              onClick={onAskDelete}
            >
              Delete
            </button>
          </>
        )}
        {mode === 'confirm-delete' && (
          <span
            data-testid={`arc-participation-confirm-delete-${participation.id}`}
            className="inline-flex items-center gap-2"
          >
            <button
              type="button"
              data-testid={`arc-participation-confirm-delete-yes-${participation.id}`}
              className="rounded bg-rose-700 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Confirm delete'}
            </button>
            <button
              type="button"
              data-testid={`arc-participation-confirm-delete-cancel-${participation.id}`}
              className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200"
              onClick={onCancelDelete}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </button>
          </span>
        )}
        {networkError && (
          <p
            data-testid={`arc-participation-row-error-${participation.id}`}
            className="mt-1 text-xs text-rose-300"
          >
            {networkError}
          </p>
        )}
      </td>
    </tr>
  )
}

interface EditParticipationRowProps {
  arcId: string
  participation: ArcParticipationAdminItem
  onCancel: () => void
  onSaved: () => void
}

function EditParticipationRow({
  arcId,
  participation,
  onCancel,
  onSaved,
}: EditParticipationRowProps) {
  const queryClient = useQueryClient()
  const idempotencyKey = useMemo(() => globalThis.crypto.randomUUID(), [participation.id])

  // Role is no longer operator-editable on this surface — see the
  // Arc participant taxonomy correction. The DB column stays as-is
  // on edit (we just don't include it in the delta).
  const [status, setStatus] = useState<ParticipationStatusEditable>(
    (PARTICIPATION_STATUSES_EDITABLE as readonly string[]).includes(participation.status)
      ? (participation.status as ParticipationStatusEditable)
      : 'active',
  )
  const [joinedAt, setJoinedAt] = useState<string>(
    participation.joined_at !== null ? participation.joined_at.slice(0, 10) : '',
  )
  const [leftAt, setLeftAt] = useState<string>(
    participation.left_at !== null ? participation.left_at.slice(0, 10) : '',
  )
  const [notes, setNotes] = useState<string>(participation.notes ?? '')

  // Per-arc kind override (migration 0013). Sentinel '' means
  // "no override; use entity.kind". A real EntityKindAdmin string
  // means override to that category.
  const [kindOverride, setKindOverride] = useState<EntityKindAdmin | ''>(
    participation.kind_override ?? '',
  )

  // Per-arc display alias (migration 0014). The "Use alias" checkbox
  // controls null vs. set; when checked, `displayAliasId` carries the
  // selected entity_aliases.id (string of bigint). On submit, the
  // pair is collapsed to a single nullable `display_alias_id` value
  // sent to the worker.
  const [useDisplayAlias, setUseDisplayAlias] = useState<boolean>(
    participation.display_alias_id !== null,
  )
  const [displayAliasId, setDisplayAliasId] = useState<string>(
    participation.display_alias_id ?? '',
  )

  // Aliases for the picker — fetched lazily once the row enters edit
  // mode (this component only renders in edit mode, so the query is
  // implicitly gated by the row state machine in `ParticipationRow`).
  const aliasesQuery = useQuery({
    queryKey: ['entity-aliases', 'admin', participation.entity_id],
    queryFn: () => fetchEntityAliasesForAdmin(participation.entity_id),
  })
  const aliasOptions = useMemo<
    Array<{ id: string; alias: string; is_primary: boolean }>
  >(() => {
    const r = aliasesQuery.data
    if (!r || r.kind !== 'ok') return []
    return r.artifact.body.aliases.map((a) => ({
      id: a.id,
      alias: a.alias,
      is_primary: a.is_primary,
    }))
  }, [aliasesQuery.data])

  const [validationError, setValidationError] =
    useState<ArcParticipationValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)

  const mutation = useMutation<
    ArcParticipationMutationFetchResult<ArcParticipationMutationReceipt>,
    unknown,
    ArcParticipationEditRequest
  >({
    mutationFn: (body) => patchArcParticipation(participation.id, { body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        queryClient.invalidateQueries({
          queryKey: ['arc', 'admin', arcId, 'participations'],
        })
        // Refresh the arc edit form's loaded snapshot so the
        // winner_entity_id field reflects any side-effect.
        queryClient.invalidateQueries({ queryKey: ['arc', 'admin', arcId] })
        // Refinement Phase B4 — keep the entity-side participations
        // view in sync. Editing a participation here flips the
        // matching row on EntityEdit.tsx.
        queryClient.invalidateQueries({
          queryKey: ['entity-participations', participation.entity_id],
        })
        onSaved()
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setNetworkError('This participation no longer exists.')
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setValidationError(null)
    setNetworkError(null)
    const body: ArcParticipationEditRequest = { idempotency_key: idempotencyKey }
    // role intentionally omitted — no longer operator-editable.
    if (status !== participation.status) body.status = status
    const newJoined = joinedAt.trim() === '' ? null : joinedAt
    const newLeft = leftAt.trim() === '' ? null : leftAt
    if (newJoined !== participation.joined_at) body.joined_at = newJoined
    if (newLeft !== participation.left_at) body.left_at = newLeft
    const newNotes = notes.trim() === '' ? null : notes
    if (newNotes !== participation.notes) body.notes = newNotes
    // kind_override: '' sentinel → null (clear); non-empty → set to that value.
    const newKindOverride: EntityKindAdmin | null =
      kindOverride === '' ? null : kindOverride
    if (newKindOverride !== participation.kind_override) {
      body.kind_override = newKindOverride
    }
    // display_alias_id (migration 0014): checkbox off → null; on +
    // selected id → that id. Only include in the patch when changed
    // from the participation's current value.
    const newDisplayAliasId: string | null = useDisplayAlias
      ? displayAliasId === ''
        ? null
        : displayAliasId
      : null
    if (newDisplayAliasId !== participation.display_alias_id) {
      body.display_alias_id = newDisplayAliasId
    }

    // Defensive: forbid submitting "Use alias" with no selection
    // (the checkbox is checked but the dropdown wasn't picked).
    // Surface as a typed validation error so the inline help text
    // appears next to the picker.
    if (useDisplayAlias && displayAliasId === '') {
      setValidationError({ code: 'invalid_alias_id', field: 'display_alias_id' })
      return
    }

    const editableKeys = Object.keys(body).filter((k) => k !== 'idempotency_key')
    if (editableKeys.length === 0) {
      setValidationError({ code: 'no_editable_fields' })
      return
    }
    mutation.mutate(body)
  }

  return (
    <tr
      data-testid={`arc-participation-edit-row-${participation.id}`}
      data-entity-slug={participation.entity.slug}
      id={`participation-${participation.id}`}
    >
      <td className="px-3 py-2 text-slate-100">
        {participation.entity.display_name}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-slate-300">
        {participation.entity.slug}
      </td>
      <td
        data-testid={`arc-participation-edit-category-${participation.id}`}
        data-entity-kind={participation.entity.kind}
        className="px-3 py-2 text-slate-300"
      >
        <select
          data-testid={`arc-participation-edit-kind-override-${participation.id}`}
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={kindOverride}
          onChange={(e) =>
            setKindOverride(e.target.value as EntityKindAdmin | '')
          }
          disabled={mutation.isPending}
        >
          <option value="">
            (use entity kind: {entityKindLabel(participation.entity.kind)})
          </option>
          {ENTITY_KINDS_ADMIN.map((k) => (
            <option key={k} value={k}>
              {entityKindLabel(k)}
            </option>
          ))}
        </select>
        <p
          data-testid={`arc-participation-edit-kind-override-help-${participation.id}`}
          className="mt-1 text-[10px] text-slate-500"
        >
          Per-arc override. Leave on "use entity kind" unless this
          entity functions as a different category specifically in
          this arc.
        </p>
      </td>
      <td
        data-testid={`arc-participation-edit-display-alias-${participation.id}`}
        className="px-3 py-2 text-slate-300"
      >
        <label className="flex items-center gap-1 text-[11px] text-slate-200">
          <input
            type="checkbox"
            data-testid={`arc-participation-edit-display-alias-checkbox-${participation.id}`}
            checked={useDisplayAlias}
            onChange={(e) => {
              const next = e.target.checked
              setUseDisplayAlias(next)
              if (!next) setDisplayAliasId('')
            }}
            disabled={mutation.isPending}
          />
          <span>Use alias</span>
        </label>
        {useDisplayAlias && (
          <>
            <select
              data-testid={`arc-participation-edit-display-alias-select-${participation.id}`}
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100 disabled:opacity-50"
              value={displayAliasId}
              onChange={(e) => setDisplayAliasId(e.target.value)}
              disabled={mutation.isPending || aliasOptions.length === 0}
            >
              <option value="">— pick alias —</option>
              {aliasOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.alias}{a.is_primary ? ' (primary)' : ''}
                </option>
              ))}
            </select>
            {aliasesQuery.isPending && (
              <p className="mt-1 text-[10px] text-slate-500">
                Loading aliases…
              </p>
            )}
            {!aliasesQuery.isPending && aliasOptions.length === 0 && (
              <p
                data-testid={`arc-participation-edit-display-alias-empty-${participation.id}`}
                className="mt-1 text-[10px] text-amber-400"
              >
                This entity has no aliases. Add one on the entity edit
                page first, then return here to select it.
              </p>
            )}
          </>
        )}
        {validationError?.code === 'invalid_alias_id' && (
          <p className="mt-1 text-[10px] text-rose-400">
            Pick an alias from the list, or uncheck "Use alias".
          </p>
        )}
      </td>
      <td className="px-3 py-2">
        <select
          data-testid={`arc-participation-edit-status-${participation.id}`}
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={status}
          onChange={(e) => setStatus(e.target.value as ParticipationStatusEditable)}
          disabled={mutation.isPending}
        >
          {PARTICIPATION_STATUSES_EDITABLE.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <p
          data-testid={`arc-participation-edit-status-help-${participation.id}`}
          className="mt-1 text-[10px] text-slate-500"
        >
          Selecting "winner" updates the arc's winner field
          atomically. Switching off "winner" clears the arc's
          field if it points at this entity.
        </p>
      </td>
      <td className="px-3 py-2">
        <input
          data-testid={`arc-participation-edit-joined-at-${participation.id}`}
          type="date"
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={joinedAt}
          onChange={(e) => setJoinedAt(e.target.value)}
          disabled={mutation.isPending}
        />
      </td>
      <td className="px-3 py-2">
        <input
          data-testid={`arc-participation-edit-left-at-${participation.id}`}
          type="date"
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={leftAt}
          onChange={(e) => setLeftAt(e.target.value)}
          disabled={mutation.isPending}
        />
      </td>
      <td className="px-3 py-2">
        <textarea
          data-testid={`arc-participation-edit-notes-${participation.id}`}
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={mutation.isPending}
          maxLength={1000}
          rows={1}
        />
        {validationError && (
          <p
            data-testid={`arc-participation-edit-error-${participation.id}`}
            data-error-code={validationError.code}
            className="mt-1 text-[10px] text-rose-300"
          >
            {validationError.code}
          </p>
        )}
        {networkError && (
          <p
            data-testid={`arc-participation-edit-network-error-${participation.id}`}
            className="mt-1 text-[10px] text-rose-300"
          >
            {networkError}
          </p>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <form
          onSubmit={handleSubmit}
          data-testid={`arc-participation-edit-form-${participation.id}`}
          className="inline-flex items-center gap-2"
        >
          <button
            type="submit"
            data-testid={`arc-participation-edit-submit-${participation.id}`}
            className="rounded bg-fishstank-accent px-2 py-1 text-xs font-semibold text-slate-900 disabled:opacity-50"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            data-testid={`arc-participation-edit-cancel-${participation.id}`}
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
        </form>
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Refinement Phase B2 — Add participation form.
//
// Renders inside <ParticipationsSection>, above the read-only
// table. Posts to POST /v2/staging/arc-participations.
//
// Idempotency: one crypto.randomUUID() per form open, reused across
// retries. After a successful create, the form regenerates its key
// (new useState seed) so the next "Add" click is a fresh row, not
// a replay of the one we just made.
//
// Entity picker: uses CP17's existing entities admin list, then
// filters out soft-merged rows client-side (the
// EntitiesAdminListItem type carries merged_into_entity_id, which
// the loader populates for every row including merged ones).
//
// 'winner' caveat (per the audit + plan):
//   - The form ACCEPTS status='winner'; the worker INSERTs the row
//     with status='winner' verbatim.
//   - The worker does NOT update arcs.winner_entity_id. The status
//     <select>'s help text spells this out: operators who want the
//     arc's winner field to update should use the upcoming edit
//     flow (B3).
// ────────────────────────────────────────────────────────────────────────

function newIdempotencyKeyForForm(): string {
  return globalThis.crypto.randomUUID()
}

function fieldFromParticipationError(
  err: ArcParticipationValidationError,
): string | null {
  switch (err.code) {
    case 'arc_id_required':
    case 'invalid_arc_id':
    case 'unknown_arc_id':
      return 'arc_id'
    case 'entity_id_required':
    case 'invalid_entity_id':
    case 'unknown_entity_id':
    case 'entity_soft_merged':
    case 'participation_already_exists':
      return 'entity_id'
    case 'role_required':
      return 'role'
    case 'status_required':
      return 'status'
    case 'invalid_enum':
      return err.field
    case 'invalid_date':
      return err.field
    case 'left_before_joined':
      return 'left_at'
    case 'notes_too_long':
      return 'notes'
    case 'invalid_uuid':
    case 'idempotency_key_in_use':
    case 'invalid_body':
      return null
    default:
      return null
  }
}

function participationErrorMessage(
  err: ArcParticipationValidationError,
): string {
  switch (err.code) {
    case 'invalid_body':
      return 'Request body must be a JSON object.'
    case 'arc_id_required':
      return 'Internal error: arc id is missing. Refresh the page.'
    case 'invalid_arc_id':
      return 'Internal error: arc id is malformed. Refresh the page.'
    case 'entity_id_required':
      return 'Pick an entity.'
    case 'invalid_entity_id':
      return 'Pick a valid entity.'
    case 'role_required':
      return 'Pick a role.'
    case 'status_required':
      return 'Pick a status.'
    case 'invalid_enum':
      return `"${err.field}" is not allowed (expected one of: ${err.allowed.join(', ')}).`
    case 'invalid_uuid':
      return 'Internal error: idempotency key must be a UUID. Refresh the page.'
    case 'invalid_date':
      return `"${err.field}" must be a date in YYYY-MM-DD format.`
    case 'left_before_joined':
      return 'Left date must be on or after Joined date.'
    case 'notes_too_long':
      return `Notes are too long (max ${err.max} characters).`
    case 'unknown_arc_id':
      return 'This arc no longer exists. Refresh the page.'
    case 'unknown_entity_id':
      return 'That entity no longer exists. Pick a different one.'
    case 'entity_soft_merged':
      return 'That entity is soft-merged and cannot be linked. Pick a different one.'
    case 'participation_already_exists':
      return 'This entity is already participating in this arc.'
    case 'idempotency_key_in_use':
      return 'This form was already submitted for a different request. Refresh the page.'
    default:
      return 'Validation failed.'
  }
}

interface AddParticipationFormProps {
  arcId: string
}

function AddParticipationForm({ arcId }: AddParticipationFormProps) {
  const queryClient = useQueryClient()

  // Idempotency key — pinned per "form-open lifecycle" via state
  // so a successful create can rotate the key for the next add.
  const [idempotencyKey, setIdempotencyKey] = useState<string>(
    () => newIdempotencyKeyForForm(),
  )

  const [entityId, setEntityId] = useState<string>('')
  // Role is no longer operator-editable — sent as the default
  // ('competitor') verbatim. See PARTICIPATION_ROLE_DEFAULT.
  const [status, setStatus] = useState<ParticipationStatusEditable>('active')
  const [joinedAt, setJoinedAt] = useState<string>('')
  const [leftAt, setLeftAt] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  // Per-arc kind override (migration 0013). '' sentinel = no override
  // (UI falls back to entity.kind on render).
  const [kindOverride, setKindOverride] = useState<EntityKindAdmin | ''>('')

  const [validationError, setValidationError] =
    useState<ArcParticipationValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [recentlyAddedEntityName, setRecentlyAddedEntityName] = useState<string | null>(null)

  // Entity picker source (CP17 admin list). Filters merged rows
  // client-side. The list is bounded server-side at 500 rows.
  const entitiesQuery = useQuery({
    queryKey: ['entities', 'admin', 'list', 'for-arc-participation-picker'],
    queryFn: fetchEntitiesForAdmin,
  })
  const pickerEntities: readonly EntitiesAdminListItem[] = (() => {
    const r = entitiesQuery.data
    if (!r || (r.kind !== 'ok' && r.kind !== 'empty')) return []
    return r.artifact.body.entities.filter(
      (e) => e.merged_into_entity_id === null,
    )
  })()

  const mutation = useMutation<
    ArcParticipationMutationFetchResult<ArcParticipationMutationReceipt>,
    unknown,
    ArcParticipationCreateRequest
  >({
    mutationFn: (body) => postArcParticipation({ body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        setRecentlyAddedEntityName(res.artifact.body.row.entity.display_name)
        // Refresh the list query so the new row appears below.
        queryClient.invalidateQueries({
          queryKey: ['arc', 'admin', arcId, 'participations'],
        })
        // Refinement Phase B4 — keep the entity-side participations
        // view in sync. Adding a participation here makes the new
        // row appear on EntityEdit.tsx for the linked entity.
        queryClient.invalidateQueries({
          queryKey: ['entity-participations', res.artifact.body.row.entity_id],
        })
        // Clear form fields and rotate the idempotency key so the
        // next click is a fresh add (not a replay of this one).
        setEntityId('')
        setStatus('active')
        setJoinedAt('')
        setLeftAt('')
        setNotes('')
        setKindOverride('')
        setIdempotencyKey(newIdempotencyKeyForForm())
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setNetworkError('This participation no longer exists.')
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setValidationError(null)
    setNetworkError(null)
    setRecentlyAddedEntityName(null)
    if (entityId === '') {
      setValidationError({ code: 'entity_id_required', field: 'entity_id' })
      return
    }
    const body: ArcParticipationCreateRequest = {
      arc_id: arcId,
      entity_id: entityId,
      // role is no longer operator-editable; the worker contract
      // still requires a value, so send the canonical default.
      role: PARTICIPATION_ROLE_DEFAULT,
      status,
      idempotency_key: idempotencyKey,
    }
    if (joinedAt.trim() !== '') body.joined_at = joinedAt
    if (leftAt.trim() !== '') body.left_at = leftAt
    if (notes.trim() !== '') body.notes = notes
    if (kindOverride !== '') body.kind_override = kindOverride
    mutation.mutate(body)
  }

  const errorField = validationError
    ? fieldFromParticipationError(validationError)
    : null

  return (
    <div
      data-testid={`arc-participation-add-form-container-${arcId}`}
      className="mb-6 rounded-md border border-slate-800 bg-fishstank-surface/40 p-4"
    >
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-400">
        Add participant
      </h3>
      <form
        data-testid={`arc-participation-add-form-${arcId}`}
        className="grid gap-3 md:grid-cols-2"
        onSubmit={handleSubmit}
      >
        <div className="md:col-span-2">
          <label
            htmlFor="arc-participation-entity"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Entity
          </label>
          <select
            id="arc-participation-entity"
            data-testid="arc-participation-add-entity"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            disabled={mutation.isPending || entitiesQuery.isLoading}
            required
          >
            <option value="">— select an entity —</option>
            {pickerEntities.map((e) => (
              <option key={e.id} value={e.id} data-entity-slug={e.slug}>
                {e.primary_name} ({e.slug})
              </option>
            ))}
          </select>
          {errorField === 'entity_id' && validationError && (
            <p
              data-testid="arc-participation-add-error-entity"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {participationErrorMessage(validationError)}
            </p>
          )}
        </div>

        {/*
          Role <select> intentionally removed — see Arc participant
          taxonomy correction. The category an operator cares about
          for a participant is the entity's `kind`, which is rendered
          in the read-only Category column on the table below and
          edited via the entity (not the participation).

          Per-arc category override (migration 0013) is offered as an
          OPTIONAL field below — leave on "use entity kind" unless
          this entity functions as a different category specifically
          in this arc.
        */}

        <div className="md:col-span-2">
          <label
            htmlFor="arc-participation-kind-override"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Per-arc category override
          </label>
          <select
            id="arc-participation-kind-override"
            data-testid="arc-participation-add-kind-override"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={kindOverride}
            onChange={(e) =>
              setKindOverride(e.target.value as EntityKindAdmin | '')
            }
            disabled={mutation.isPending}
          >
            <option value="">(use entity kind — no override)</option>
            {ENTITY_KINDS_ADMIN.map((k) => (
              <option key={k} value={k}>
                {entityKindLabel(k)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">
            Optional. Only set when this entity acts as a different
            category in this arc than its global kind. E.g. Burt is a
            fish entity globally but appears as a freeloader in S5.
          </p>
        </div>

        <div>
          <label
            htmlFor="arc-participation-status"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Status
          </label>
          <select
            id="arc-participation-status"
            data-testid="arc-participation-add-status"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as ParticipationStatusEditable)
            }
            disabled={mutation.isPending}
            required
          >
            {PARTICIPATION_STATUSES_EDITABLE.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <p
            data-testid="arc-participation-add-status-help"
            className="mt-1 text-xs text-slate-500"
          >
            Selecting "winner" records the row but does NOT update the
            arc's denormalised winner field. Use the upcoming edit
            flow (B3) to set the arc's winner.
          </p>
          {errorField === 'status' && validationError && (
            <p
              data-testid="arc-participation-add-error-status"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {participationErrorMessage(validationError)}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="arc-participation-joined-at"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Joined
          </label>
          <input
            id="arc-participation-joined-at"
            data-testid="arc-participation-add-joined-at"
            type="date"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={joinedAt}
            onChange={(e) => setJoinedAt(e.target.value)}
            disabled={mutation.isPending}
          />
          {errorField === 'joined_at' && validationError && (
            <p
              data-testid="arc-participation-add-error-joined-at"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {participationErrorMessage(validationError)}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="arc-participation-left-at"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Left
          </label>
          <input
            id="arc-participation-left-at"
            data-testid="arc-participation-add-left-at"
            type="date"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={leftAt}
            onChange={(e) => setLeftAt(e.target.value)}
            disabled={mutation.isPending}
          />
          {errorField === 'left_at' && validationError && (
            <p
              data-testid="arc-participation-add-error-left-at"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {participationErrorMessage(validationError)}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label
            htmlFor="arc-participation-notes"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Notes
          </label>
          <textarea
            id="arc-participation-notes"
            data-testid="arc-participation-add-notes"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={mutation.isPending}
            maxLength={1000}
            rows={2}
          />
          {errorField === 'notes' && validationError && (
            <p
              data-testid="arc-participation-add-error-notes"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {participationErrorMessage(validationError)}
            </p>
          )}
        </div>

        {validationError && errorField === null && (
          <div
            data-testid="arc-participation-add-validation-error"
            data-error-code={validationError.code}
            className="md:col-span-2 rounded border border-rose-700 bg-rose-950/40 p-3 text-rose-100"
          >
            <strong>{validationError.code}</strong>
            <span className="ml-2">{participationErrorMessage(validationError)}</span>
          </div>
        )}

        {networkError && (
          <div
            data-testid="arc-participation-add-network-error"
            className="md:col-span-2 rounded border border-rose-700 bg-rose-950/40 p-3 text-rose-100"
          >
            {networkError}
          </div>
        )}

        {recentlyAddedEntityName && (
          <div
            data-testid="arc-participation-add-receipt"
            className="md:col-span-2 rounded border border-emerald-700 bg-emerald-950/40 p-3 text-emerald-100"
          >
            Added{' '}
            <strong data-testid="arc-participation-add-receipt-name">
              {recentlyAddedEntityName}
            </strong>{' '}
            to this arc.
          </div>
        )}

        <div className="md:col-span-2">
          <button
            type="submit"
            data-testid="arc-participation-add-submit"
            className="rounded bg-fishstank-accent px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Adding…' : 'Add participant'}
          </button>
        </div>
      </form>
    </div>
  )
}
